import React, { useState, useEffect, useRef } from 'react';
import { Loader2, MessageSquare, SortDesc } from 'lucide-react';
import { discussionService } from '@/services/discussion.service';
import type { ForumDiscussionDto, DiscussionSortBy } from '@/types/discussion.types';
import DiscussionForm, { type DiscussionFormRef } from './DiscussionForm';
import DiscussionItem from './DiscussionItem';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import toast from 'react-hot-toast';

// The props interface remains the same, accepting 'sortBy' from the parent
interface DiscussionListProps {
  threadId: string;
  threadTitle?: string;
  sortBy: DiscussionSortBy;
   onCommentPosted: () => void; 
  onCommentDeleted: () => void;
}

export default function DiscussionList({ threadId, threadTitle, sortBy,onCommentPosted,onCommentDeleted }: DiscussionListProps) {
  const [discussions, setDiscussions] = useState<ForumDiscussionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [justPostedId, setJustPostedId] = useState<string | null>(null);
  const formRef = useRef<DiscussionFormRef>(null);

  const limit = 10;

  // --- (All functions remain the same as the last correct step) ---
  const loadDiscussions = async (reset = false) => {
    try {
      const currentPage = reset ? 1 : page;
      if (reset) 
        setLoading(true); 
      else setLoadingMore(true);
      if (!threadId)
         { toast.error('Thread ID is missing'); return; }
      const response = await discussionService.getTopLevelDiscussions(threadId, currentPage, limit, sortBy);
      if (!response) 
        { toast.error('Invalid response from server'); return; }
      const loadedDiscussions = response.discussions || [];
      if (reset) {
        setDiscussions(loadedDiscussions);
        setPage(1);
      } else {
        setDiscussions([...discussions, ...loadedDiscussions]);
      }
      setTotal(response.total || 0);
      setHasMore(loadedDiscussions.length === limit);
    } catch (error: any) {
      toast.error(error?.response?.data?.description || error?.message || 'Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { loadDiscussions(true); }, [threadId, sortBy]);
  const handleLoadMore = () => { setPage(page + 1); loadDiscussions(false); };
  const handleNewDiscussion = (newDiscussion: ForumDiscussionDto) => { 
    if (sortBy === 'newest') {
       setDiscussions([newDiscussion, ...discussions]);
       setTotal(total + 1); 
       setJustPostedId(newDiscussion._id);
        onCommentPosted();
        setTimeout(() => setJustPostedId(null), 3000); } 
        else { loadDiscussions(true); } };
     const handleUpdateDiscussion = (updated: ForumDiscussionDto) => { setDiscussions(discussions.map((d) => (d._id === updated._id ? updated : d))); };
     const handleDeleteDiscussion = (discussionId: string) => { 
      setDiscussions(discussions.filter((d) => d._id !== discussionId));
      setTotal(Math.max(0, total - 1)); 
        onCommentDeleted(); 
    };
     const handleReply = (discussion: ForumDiscussionDto) => { setDiscussions(discussions.map((d) => d._id === discussion._id ? 
    { ...d, reply_count: d.reply_count + 1 } : d)); };


  return (
    <>
     
      {/*
      <div className="flex flex-col h-full bg-white">
        // Header
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200">
          // ...
        </div>

        // Discussion list was here, in the middle
        <div className="flex-1 overflow-y-auto">
          // ...
        </div>

        // Comment form was here, at the bottom
        <div className="flex-shrink-0 px-6 py-4 bg-white border-t border-gray-200">
          <DiscussionForm // ...
          />
        </div>
      </div>
      */}
      
      <div className="flex flex-col h-full bg-white">
      
        {/* <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-brand-indigo-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Discussion
                </h2>
                <p className="text-sm text-gray-600 -mt-1">
                  {total === 0 ? 'No comments yet' : `${total} ${total === 1 ? 'comment' : 'comments'}`}
                </p>
              </div>
            </div>
          </div>
        </div> */}


        <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200">
          <DiscussionForm
            ref={formRef}
            threadId={threadId}
            onSuccess={handleNewDiscussion}
            placeholder="Add a comment..."
          />
        </div>

        {/* Discussion List - Fills the remaining space */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="divide-y divide-gray-200">{Array.from({ length: 5 }).map((_, index) => (<div key={index}
               className="px-6 py-4">
                <div className="flex gap-3"><SkeletonLoader type="avatar" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2"><SkeletonLoader type="custom" className="h-4 w-32" />
                  <SkeletonLoader type="custom" className="h-3 w-16" />
                  </div><SkeletonLoader type="text" count={2} />
                  <div className="flex items-center gap-3">
                    <SkeletonLoader type="custom" className="h-4 w-16" />
                    <SkeletonLoader type="custom" className="h-4 w-16" />
                    </div>
                    </div>
                    </div>
                    </div>
                    )
                    )}
                    </div>
          ) : discussions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <div className="bg-brand-indigo-50 rounded-full p-4 mb-4">
                <MessageSquare className="w-10 h-10 text-brand-indigo-500" />
                </div><p className="text-gray-900 font-semibold text-lg mb-2">Start the Conversation</p>
                <p className="text-sm text-gray-500 mb-4 max-w-sm">Be the first to share your thoughts, ask questions, or provide feedback on this thread.</p>
                <button onClick={() => formRef.current?.focus()} 
                 className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-indigo-600 text-white text-sm font-medium rounded-full hover:bg-brand-indigo-700 transition-all hover:shadow-md">
                  <MessageSquare className="w-4 h-4" />Add a comment</button>
                  </div>
          ) : (
            <div className="divide-y divide-gray-200">{discussions.map((discussion) => (<div key={discussion._id} 
              className={`px-6 transition-all duration-1000 ${justPostedId === discussion._id ? 'bg-brand-indigo-50/50' : ''}`}>
                <DiscussionItem
                 discussion={discussion} 
                 threadId={threadId} 
                onUpdate={handleUpdateDiscussion} 
                onDelete={handleDeleteDiscussion} 
                onReply={handleReply} />
                </div>
                ))}
                {hasMore && (<div className="px-6 py-4 text-center">
                  <button onClick={handleLoadMore} 
                  disabled={loadingMore} 
                  className="px-4 py-2 text-sm font-medium text-brand-indigo-600 hover:text-brand-indigo-700 hover:bg-brand-indigo-50 rounded-lg">
                    {loadingMore ? (<span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />Loading...</span>) : ('Load More Comments')}
                      </button>
                      </div>
                      )}
                      </div>
             )}
        </div>
      </div>
    </>
  );
}